/**
 * The bell — §7.8, roadmap 17.5.
 *
 * In the shell header, so all five portals get one bell, one poll and one
 * badge. The list itself is a Radix dropdown: a menu is the right primitive
 * because every row is an action (marking it read), and Radix gives the focus
 * trap, the roving focus and the escape handling that a hand-rolled panel
 * always ends up missing.
 *
 * ⚠️ **The badge polls; the list does not.** `useUnreadCount` runs on a 60s
 * interval whether or not the menu is open, and `useNotifications` is `enabled`
 * only while it is. Polling the list too would fetch twenty rows a minute for a
 * panel nobody has open — the count is one integer and is what the badge needs.
 */
import { Bell } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { useSession } from '@/shared/auth/useSession'
import { formatDateTime, formatNumber } from '@/shared/i18n/format'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
  type Notification,
} from '@/shared/notifications/hooks'
import {
  Button,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  messageFor,
} from '@/shared/ui'

/**
 * Above this the badge reads `9+`.
 *
 * A three-digit count in a 20px circle is unreadable and pushes the header
 * around as it grows. Past a handful the exact number stops being information
 * anyway — the answer to "how many" is "more than you are going to read now".
 */
const BADGE_CAP = 9

export function NotificationBell() {
  const { t, i18n } = useTranslation('common')
  const session = useSession()
  const [open, setOpen] = useState(false)

  // ⚠️ Anonymous and still-loading sessions must not poll. Without this a
  // signed-out browser asks every minute and gets a 401 every minute, each one
  // provoking the client's refresh-on-401 against a session that does not
  // exist.
  const signedIn = session.status === 'authenticated'

  const count = useUnreadCount({ enabled: signedIn })
  const list = useNotifications({ enabled: signedIn && open })
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  if (!signedIn) return null

  const unread = count.data?.unread ?? 0
  const rows = list.data?.results ?? []

  return (
    <DropdownMenuRoot open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          // ⚠️ The count belongs in the accessible name, not only in the badge.
          // A screen reader otherwise announces "Notifications" whether there
          // are none or nine, and the badge is a decorative span it skips.
          aria-label={
            unread > 0
              ? t('notifications.openWithCount', { count: unread })
              : t('notifications.open')
          }
        >
          <Bell aria-hidden="true" />
          {unread > 0 && (
            <span
              // Positioned with logical properties — `end` is the right edge in
              // English and the left in Arabic, which is where the eye leaves
              // the icon in each.
              className="absolute -top-0.5 -end-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] leading-none font-semibold text-fg-ondanger"
              // Announced through the button's own label above, so the badge
              // itself is decorative and must not be read twice.
              aria-hidden="true"
            >
              {unread > BADGE_CAP
                ? t('notifications.countOverflow', {
                    count: BADGE_CAP,
                    value: formatNumber(BADGE_CAP, i18n.language),
                  })
                : formatNumber(unread, i18n.language)}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-80 max-w-[calc(100vw-2rem)]">
        <div className="flex items-center justify-between gap-2 px-2.5 py-2">
          <p className="text-sm font-semibold">{t('notifications.title')}</p>
          {unread > 0 && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              loading={markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>

        <DropdownMenuSeparator />

        {list.isPending ? (
          <p className="px-2.5 py-6 text-center text-sm text-fg-muted">{t('state.loading')}</p>
        ) : list.isError ? (
          <p className="px-2.5 py-6 text-center text-sm text-fg-muted">
            {messageFor(list.error, t('state.error'))}
          </p>
        ) : rows.length === 0 ? (
          <p className="px-2.5 py-6 text-center text-sm text-fg-muted">
            {t('notifications.empty')}
          </p>
        ) : (
          <ul className="max-h-80 overflow-y-auto">
            {rows.map((row) => (
              <li key={row.id}>
                <NotificationRow
                  notification={row}
                  locale={i18n.language}
                  unreadLabel={t('notifications.unread')}
                  onMarkRead={() => markRead.mutate(row.id)}
                />
              </li>
            ))}
          </ul>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          {/* The way to the switches, which is what someone opening this menu
              to complain about it is looking for. */}
          <Link to="/me/notifications" className="text-sm">
            {t('notifications.settings')}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  )
}

function NotificationRow({
  notification,
  locale,
  unreadLabel,
  onMarkRead,
}: {
  notification: Notification
  locale: string
  unreadLabel: string
  onMarkRead: () => void
}) {
  const isUnread = notification.read_at === null

  return (
    <button
      type="button"
      // ⚠️ `disabled` on a read row, not a hidden click handler. A row that
      // looks pressable and does nothing reads as a broken control; one that is
      // visibly inert does not invite the press.
      disabled={!isUnread}
      onClick={onMarkRead}
      className="flex w-full items-start gap-2 px-2.5 py-2 text-start hover:bg-sunken disabled:hover:bg-transparent"
    >
      {/* Colour is never the only signal (NFR-A11y) — the dot carries an
          `sr-only` word so the state is readable, not merely visible.
          ⚠️ The dot is `brand-fg`, not `brand-600`: it is the only *visual*
          cue, so it owes 3:1 to the raised panel — dark 600 is 2.26:1. */}
      <span className="mt-1.5 flex size-2 shrink-0 items-center justify-center">
        {isUnread && (
          <>
            <span className="size-2 rounded-full bg-brand-fg" aria-hidden="true" />
            <span className="sr-only">{unreadLabel}</span>
          </>
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm ${isUnread ? 'font-semibold' : 'text-fg-muted'}`}>
          {notification.title}
        </span>
        {notification.body && (
          <span className="mt-0.5 block truncate text-xs text-fg-muted">{notification.body}</span>
        )}
        <span className="mt-0.5 block text-xs text-fg-subtle">
          {formatDateTime(notification.created_at, locale)}
        </span>
      </span>
    </button>
  )
}
